import { FC, useCallback } from 'react'
import { SettingsPageLayout } from '../components/SettingsPageLayout'
import { useTeamSettings } from 'SettingsV2/components/team/useTeamSettings'
import { Member } from '@stytch/react/b2b'
import { useSearchSettings } from 'SettingsV2/hooks/useSearchSettings'
import {
  Text,
  Badge,
  Button,
  EmptyState,
  GreyGrey70,
  IconUserGroup,
  IndigoIndigo60,
  Link,
  Primary13Regular,
  SequenceMenu,
  SequenceKebabMenuItem,
  SettingsFormControl,
  SettingsGroup,
  SettingsGroupSearch,
  highlightQueryMatch,
  useNotifications,
  Flex
} from '@sequencehq/core-components'
import { RevokeInviteModal } from 'SettingsV2/components/team/Modals/RevokeInviteModal'
import { DeleteMemberModal } from 'SettingsV2/components/team/Modals/DeleteMemberModal'
import { InviteMemberModalV2 } from 'SettingsV2/components/team/Modals/InviteMemberModalV2'
import { EditMemberModal } from 'SettingsV2/components/team/Modals/EditMemberModal'
import { getSystemRoleLabel } from 'lib/rbac/systemRoles'
import { usePermissions } from 'lib/rbac'
import { useCsvExport } from 'lib/hooks/useCsvExport.tsx'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { format } from '@sequencehq/utils/dates'

export const TeamSettingsPageV2: FC = () => {
  const { members, user, isLoading, inviteMemberWithRoles } = useTeamSettings()
  const { isAdmin } = usePermissions()
  const sortByPendingThenCurrentUserThenEmail = useCallback(
    (filtered: Member[]) => {
      const currentUserEmail = user?.email_address ?? ''
      return [...filtered].sort((a, b) => {
        const isAPending = a.status === 'invited'
        const isBPending = b.status === 'invited'
        if (isAPending && !isBPending) return -1
        if (!isAPending && isBPending) return 1

        const isAUser = a.email_address === currentUserEmail
        const isBUser = b.email_address === currentUserEmail
        if (isAUser && !isBUser) return -1
        if (!isAUser && isBUser) return 1

        return a.email_address.localeCompare(b.email_address)
      })
    },
    [user?.email_address]
  )

  const { query, setQuery, results } = useSearchSettings<Member>(
    members ?? [],
    {
      keys: ['name', 'email_address'],
      sort: sortByPendingThenCurrentUserThenEmail
    }
  )

  const { download: activityLogsCsvDownload } = useCsvExport({
    getExport: () => dashboardv99990101Client.getActivityLogsRbacCsvDownload(),
    fileName: `${format(new Date(), 'yyyy-MM-dd')}-activity-logs-export.csv`
  })
  const { onClick: downloadActivityLogsCsv } = activityLogsCsvDownload

  return (
    <SettingsPageLayout
      width="700px"
      header="Team"
      subheader={
        <Text {...Primary13Regular} color={GreyGrey70} mt="8px">
          Manage roles and permissions for your team.{' '}
          <Link.Unstyled
            style={{ textDecoration: 'none', color: IndigoIndigo60 }}
            to="https://docs.sequencehq.com/users/roles-permissions#roles-and-permissions"
            target="_blank"
          >
            Learn more
          </Link.Unstyled>
        </Text>
      }
      actions={
        <Flex gap="8px" alignItems="center">
          {isAdmin && (
            <Button
              variant="secondary"
              onClick={() => downloadActivityLogsCsv(new URLSearchParams())}
            >
              Export activity logs
            </Button>
          )}
          <InviteMemberModalV2 />
        </Flex>
      }
    >
      <SettingsGroup
        maxWidth="700px"
        isLoading={isLoading}
        headers={[
          { label: 'User', width: '320px' },
          { label: 'Role', width: '250px' },
          { label: 'Status', width: '100px' }
        ]}
        action={
          <SettingsGroupSearch
            data-testid="teamSettings.search"
            placeholder="Search by name or email"
            value={query}
            onChange={el => setQuery(el.target.value)}
          />
        }
      >
        {results.length > 0 ? (
          results.map(member => (
            <SettingsFormControl
              key={member.member_id}
              htmlFor={member.member_id}
            >
              <Flex
                width="100%"
                gap="4px"
                height="100%"
                data-testid={`teamSettings.member.${member.member_id}`}
              >
                <Flex
                  alignItems="center"
                  gap="8px"
                  minWidth="310px"
                  maxWidth="310px"
                >
                  <Text
                    {...Primary13Regular}
                    isTruncated
                    data-testid={`teamSettings.memberEmail.${member.member_id}`}
                  >
                    {highlightQueryMatch(member.email_address, query)}
                  </Text>
                  {user?.email_address === member.email_address && (
                    <Badge>You</Badge>
                  )}
                </Flex>

                <Flex
                  gap="8px"
                  minWidth="245px"
                  maxWidth="245px"
                  paddingRight="20px"
                  alignItems="center"
                  flexWrap="wrap"
                >
                  {member.roles
                    ?.map(role => getSystemRoleLabel(role.role_id))
                    ?.filter(label => Boolean(label))
                    ?.map(role => (
                      <Badge key={role}>{role}</Badge>
                    ))}
                </Flex>

                <Flex
                  gap="8px"
                  width="100%"
                  maxWidth="100px"
                  alignItems="center"
                >
                  {member.status === 'active' && (
                    <Badge sentiment="success">Active</Badge>
                  )}
                  {member.status === 'invited' && (
                    <Badge sentiment="highlight">Pending</Badge>
                  )}
                </Flex>

                <Flex width="50px" alignItems="center">
                  <TeamMemberActions
                    member={member}
                    isAdmin={isAdmin}
                    inviteMemberWithRoles={inviteMemberWithRoles}
                  />
                </Flex>
              </Flex>
            </SettingsFormControl>
          ))
        ) : (
          <EmptyState
            title="No users found"
            icon={IconUserGroup}
            iconSize="sm"
            body={`Your search for "${query}" did not return any results.`}
          />
        )}
      </SettingsGroup>
    </SettingsPageLayout>
  )
}

type TeamMemberActionsProps = {
  member: Member
  isAdmin: boolean
  inviteMemberWithRoles: ReturnType<
    typeof useTeamSettings
  >['inviteMemberWithRoles']
}

const TeamMemberActions: FC<TeamMemberActionsProps> = ({
  member,
  isAdmin,
  inviteMemberWithRoles
}) => {
  const { displayNotification } = useNotifications()

  if (member.status === 'invited') {
    return (
      <SequenceMenu
        variant="ghost"
        buttonSize="medium"
        items={[
          {
            label: 'Resend invite',
            onClick: () => {
              inviteMemberWithRoles({
                email: member.email_address,
                roles: isAdmin ? member.roles.map(r => r.role_id) : []
              })
            }
          },
          <RevokeInviteModal
            member={member}
            key="revoke"
            trigger={
              <SequenceMenu.Item
                uuid="revoke"
                label="Revoke"
                style={{ color: 'red' }}
              />
            }
          />
        ]}
      />
    )
  }

  // Sequence user id lives in Stytch trusted metadata; absent for pending
  // invites that haven't been accepted yet.
  const sequenceUserId = member.trusted_metadata.sequenceUserId as
    | string
    | undefined

  const copyUserIdItem: SequenceKebabMenuItem = {
    label: 'Copy user ID',
    'data-testid': 'team.copyUserId',
    onClick: async () => {
      if (!sequenceUserId) return
      try {
        await navigator.clipboard.writeText(sequenceUserId)
        displayNotification('User ID copied', {
          type: 'success',
          isClosable: true,
          duration: 2000
        })
      } catch {
        displayNotification('Failed to copy user ID', {
          type: 'error',
          isClosable: true,
          duration: 2000
        })
      }
    }
  }

  const menuItems: SequenceKebabMenuItem[] = [
    ...(sequenceUserId ? [copyUserIdItem] : []),
    ...(isAdmin
      ? [
          <EditMemberModal
            member={member}
            key="edit"
            trigger={
              <SequenceMenu.Item
                uuid="edit"
                label="Edit"
                data-testid="team.edit.trigger"
              />
            }
          />,
          <DeleteMemberModal
            member={member}
            key="delete"
            trigger={
              <SequenceMenu.Item
                uuid="delete"
                label="Delete"
                style={{ color: 'red' }}
              />
            }
          />
        ]
      : [])
  ]

  if (menuItems.length === 0) {
    return null
  }

  return (
    <SequenceMenu
      variant="ghost"
      buttonSize="medium"
      data-testid="team.member.menu"
      items={menuItems}
    />
  )
}
